import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { guns } from './guns';
import AdsenseAd from './AdSense';

export type WeaponData = {
  name: string;
  dmg: number;
  pellet?: number;
  rpm: number;
  rpm_base?: number;
  accel?: number;
  rpm_1?: number;
  rpm_2?: number;
  rpm_3?: number;
  hammer?: number;
  disruptor?: number;
  delay?: number;
  burst?: number;
  burst_delay?: number;
  ammo: AmmoType;
  type: string;
  care_package?: boolean;
};

type AmmoType =
  | 'shotgun'
  | 'light'
  | 'heavy'
  | 'energy'
  | 'sniper'
  | 'arrow'
  | 'legend';

type WeaponWithStats = WeaponData & {
  dps: number;
  ttk150: number;
  ttk175: number;
  ttk200: number;
  ttk225: number;
};

const ApexDPS = () => {
  const [weapons, setWeapons] = useState<WeaponData[]>(guns);
  const [sortConfig, setSortConfig] = useState<{ key: keyof WeaponWithStats | null; direction: 'asc' | 'desc' }>({ key: 'ttk175', direction: 'asc' });
  const [shotgunLevel, setShotgunLevel] = useState<number>(2);
  const [includeDelay, setIncludeDelay] = useState<boolean>(true);

  // 実効RPMの計算（バースト武器用）
  const getEffectiveRPM = (weapon: WeaponData): number => {
    if (weapon.burst && weapon.burst_delay) {
      const timePerBurst = (weapon.burst - 1) / weapon.rpm * 60 + weapon.burst_delay;
      const burstsPerMinute = 60 / timePerBurst;
      return burstsPerMinute * weapon.burst;
    }
    if (weapon.pellet) {
      const rpmKey = shotgunLevel === 0 ? 'rpm' : `rpm_${shotgunLevel}` as 'rpm_1' | 'rpm_2' | 'rpm_3';
      return weapon[rpmKey] || weapon.rpm;
    }
    return weapon.rpm;
  };

  // 実効ダメージの計算
  const getEffectiveDamage = (weapon: WeaponData, enemyHP: number): number => {
    let baseDmg = weapon.dmg;
    if (weapon.pellet) {
      baseDmg *= weapon.pellet;
    }

    // ハンマーポイント: HP100以下の部分のダメージが増加
    if (weapon.hammer && enemyHP <= 100) {
      return baseDmg * weapon.hammer;
    }

    // ディスラプター: HP100超過の部分のダメージが増加
    if (weapon.disruptor && enemyHP > 100) {
      return baseDmg * weapon.disruptor;
    }

    return baseDmg;
  };

  // DPS計算
  const calculateDPS = (weapon: WeaponData): string => {
    const effectiveRPM = getEffectiveRPM(weapon);
    const effectiveDmg = getEffectiveDamage(weapon, 200); // 最大HP想定
    return ((effectiveDmg * effectiveRPM) / 60).toFixed(2);
  };

  // TTK計算
  const calculateTTK = (weapon: WeaponData, enemyHP: number): string => {
    let totalTime = 0;
    let remainingHP = enemyHP;
    let shotCount = 0;

    // ディレイを含める場合
    if (includeDelay && weapon.delay) {
      totalTime += weapon.delay;
    }

    // 加速武器の場合
    if (weapon.rpm_base && weapon.accel) {
      let timeFromFirstShot = 0;

      if (weapon.burst && weapon.burst_delay) {
        // 加速バースト武器
        while (remainingHP > 0) {
          for (let shotInBurst = 0; shotInBurst < weapon.burst; shotInBurst++) {
            const dmg = getEffectiveDamage(weapon, remainingHP);
            remainingHP -= dmg;
            shotCount++;

            if (shotCount === 1) continue;

            const currentRPM = Math.min(
              weapon.rpm_base + ((weapon.rpm - weapon.rpm_base) * (timeFromFirstShot / weapon.accel)),
              weapon.rpm
            );
            const shotInterval = 60 / currentRPM;
            totalTime += shotInterval;
            timeFromFirstShot += shotInterval;

            if (remainingHP <= 0) break;
          }

          if (remainingHP <= 0) break;

          totalTime += weapon.burst_delay;
          timeFromFirstShot += weapon.burst_delay;
        }
      } else {
        // 加速通常武器
        while (remainingHP > 0) {
          const dmg = getEffectiveDamage(weapon, remainingHP);
          remainingHP -= dmg;
          shotCount++;

          if (shotCount === 1) continue;

          const currentRPM = Math.min(
            weapon.rpm_base + ((weapon.rpm - weapon.rpm_base) * (timeFromFirstShot / weapon.accel)),
            weapon.rpm
          );
          const shotInterval = 60 / currentRPM;
          totalTime += shotInterval;
          timeFromFirstShot += shotInterval;

          if (remainingHP <= 0) break;
        }
      }

      return totalTime.toFixed(3);
    }

    // バースト武器
    if (weapon.burst && weapon.burst_delay) {
      while (remainingHP > 0) {
        for (let shotInBurst = 0; shotInBurst < weapon.burst; shotInBurst++) {
          const dmg = getEffectiveDamage(weapon, remainingHP);
          remainingHP -= dmg;
          shotCount++;

          if (shotCount === 1) continue;

          totalTime += 60 / weapon.rpm;

          if (remainingHP <= 0) break;
        }

        if (remainingHP <= 0) break;

        totalTime += weapon.burst_delay;
      }

      return totalTime.toFixed(3);
    }

    // 通常武器・ショットガン
    const effectiveRPM = getEffectiveRPM(weapon);

    while (remainingHP > 0) {
      const dmg = getEffectiveDamage(weapon, remainingHP);
      remainingHP -= dmg;
      shotCount++;

      if (shotCount === 1) continue;

      totalTime += 60 / effectiveRPM;

      if (remainingHP <= 0) break;
    }

    return totalTime.toFixed(3);
  };

  const weaponTypes = useMemo(() => Array.from(new Set(weapons.map(w => w.type))).sort(), [weapons]);

  const toggleTypeFilter = (type: string) => {
    setTypeFilters(prev => {
      const newFilters = new Set(prev);
      newFilters.has(type) ? newFilters.delete(type) : newFilters.add(type);
      return newFilters;
    });
  };

  const initialTypeFilters = useMemo(() => {
    return new Set(weaponTypes.filter(type => type/* !== 'MG' && type !== 'SR'*/));
  }, [weaponTypes]);
  const [typeFilters, setTypeFilters] = useState<Set<string>>(initialTypeFilters);

  // ソート処理
  const sortedWeapons = useMemo(() => {
    let filteredWeapons = typeFilters.size > 0 ? weapons.filter(w => typeFilters.has(w.type)) : weapons;
    const weaponsWithStats: WeaponWithStats[] = filteredWeapons.map(w => ({
      ...w,
      dps: parseFloat(calculateDPS(w)),
      ttk150: parseFloat(calculateTTK(w, 150)),
      ttk175: parseFloat(calculateTTK(w, 175)),
      ttk200: parseFloat(calculateTTK(w, 200)),
      ttk225: parseFloat(calculateTTK(w, 225)),
    }));
    if (!sortConfig.key) return weaponsWithStats;
    const key = sortConfig.key;
    return [...weaponsWithStats].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });
  }, [weapons, sortConfig, shotgunLevel, includeDelay, typeFilters]);

  const toggleAllTypes = () => {
    setTypeFilters(typeFilters.size === weaponTypes.length ? new Set() : new Set(weaponTypes));
  };

  const handleSort = (key: keyof WeaponWithStats) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const SortIcon = ({ column }: { column: keyof WeaponWithStats }) => {
    if (sortConfig.key !== column) return <ArrowUpDown className="w-4 h-4 opacity-40" />;
    return sortConfig.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const getAmmoGradient = (weapon: WeaponData) => {
    if (weapon.care_package) {
      return 'bg-gradient-to-r from-red-900 to-slate-800';
    }

    const ammoGradients = {
      shotgun: 'bg-gradient-to-r from-red-600 to-slate-800',
      light: 'bg-gradient-to-r from-orange-600 to-slate-800',
      heavy: 'bg-gradient-to-r from-teal-600 to-slate-800',
      energy: 'bg-gradient-to-r from-lime-400 to-slate-800',
      sniper: 'bg-gradient-to-r from-indigo-600 to-slate-800',
      arrow: 'bg-gradient-to-r from-yellow-500 to-slate-800',
      legend: 'bg-gradient-to-r from-pink-400 to-slate-800',
    };

    return ammoGradients[weapon.ammo] ?? 'bg-gradient-to-r from-slate-700 to-slate-800';
  };

  const shotgunBoltText = ["無", "白", "青", "紫/金"];
  const shotgunBoltColor = ["bg-slate-500 text-white", "bg-slate-300 text-white", "bg-blue-400 text-white", "bg-purple-600 text-white"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-slate-800 rounded-lg shadow-2xl border border-slate-700 overflow-hidden">
          {/* ヘッダー */}
          <div className="bg-gradient-to-r from-red-600 to-orange-600 p-4 lg:p-6">
            <h1 className="text-xl lg:text-3xl font-bold text-white mb-2">Apex Legends 全武器DPS・TTKリスト(シーズン29)</h1>
            <p className="text-red-100">最終更新日：2026-05-06</p>
          </div>

          {/* 設定エリア */}

          <div className="p-4 lg:p-6 bg-slate-750 border-b border-slate-700">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4">
              <div className='flex flex-row gap-2 items-baseline'>
                <label className="block text-sm font-small text-slate-300">ショットガンボルト</label>
                <div className="flex gap-2">
                  {[0, 1, 2, 3].map(level => (
                    <button key={level} onClick={() => setShotgunLevel(level)}
                      className={`px-2 py-1 font-small lg:px-4 lg:py-2 rounded-lg lg:font-medium transition-colors ${shotgunLevel === level ? shotgunBoltColor[level] : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                      {shotgunBoltText[level]}
                    </button>
                  ))}
                </div>
              </div>
              <div className='flex flex-row gap-2 items-baseline'>
                <label className="block text-sm font-small text-slate-300">ディレイ</label>
                <div className="flex gap-2">
                  <button onClick={() => setIncludeDelay(false)}
                    className={`px-2 py-1 font-small lg:px-4 lg:py-2 rounded-lg lg:font-medium transition-colors ${!includeDelay ? 'bg-yellow-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                    除外
                  </button>
                  <button onClick={() => setIncludeDelay(true)}
                    className={`px-2 py-1 font-small lg:px-4 lg:py-2 rounded-lg lg:font-medium transition-colors ${includeDelay ? 'bg-yellow-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                    含む
                  </button>
                </div>
              </div>
              <div className='col-span-full flex flex-col lg:flex-row gap-2 items-baseline'>
                <label className="block text-sm font-small text-slate-300">フィルター</label>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={toggleAllTypes}
                    className={`px-2 py-1 font-small lg:px-4 lg:py-2 rounded-lg lg:font-medium transition-colors text-sm ${typeFilters.size === 0 || typeFilters.size === weaponTypes.length ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                    {typeFilters.size === weaponTypes.length ? '全解除' : '全選択'}
                  </button>
                  {weaponTypes.map(type => (
                    <button key={type} onClick={() => toggleTypeFilter(type)}
                      className={`px-2 py-1 font-small lg:px-4 lg:py-2 rounded-lg lg:font-medium transition-colors text-sm ${typeFilters.has(type) ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className='flex flex-col-reverse lg:flex-row'>
            {/* 武器統計 */}
            <div className="p-4 lg:p-6 bg-slate-800 flex-1 min-w-0">

              {/* テーブル */}
              <div className="rounded-lg border border-slate-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-900">
                      <tr>
                        <th
                          onClick={() => handleSort('name')}
                          className="sm:sticky left-0 z-20 bg-slate-900 px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800 transition-colors border-r border-slate-700 min-w-[150px]"
                        >
                          <div className="flex items-center gap-2">
                            武器名
                            <SortIcon column="name" />
                          </div>
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                          種類
                        </th>
                        <th
                          onClick={() => handleSort('dmg')}
                          className="px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            DMG
                            <SortIcon column="dmg" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('rpm')}
                          className="px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            RPM
                            <SortIcon column="rpm" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('dps')}
                          className="px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            DPS
                            <SortIcon column="dps" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('ttk150')}
                          className="px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800 transition-colors bg-slate-850"
                        >
                          <div className="flex items-center gap-2">
                            TTK(150)
                            <SortIcon column="ttk150" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('ttk175')}
                          className="px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800 transition-colors bg-slate-850"
                        >
                          <div className="flex items-center gap-2">
                            TTK(175)
                            <SortIcon column="ttk175" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('ttk200')}
                          className="px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800 transition-colors bg-slate-850"
                        >
                          <div className="flex items-center gap-2">
                            TTK(200)
                            <SortIcon column="ttk200" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('ttk225')}
                          className="px-2 py-2 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800 transition-colors bg-slate-850"
                        >
                          <div className="flex items-center gap-2">
                            TTK(225)
                            <SortIcon column="ttk225" />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-slate-800 divide-y divide-slate-700">
                      {sortedWeapons.map((weapon, idx) => (
                        <tr key={idx} className="hover:bg-slate-750 transition-colors">
                          <td className={`sm:sticky left-0 z-10 px-2 py-2 whitespace-nowrap text-sm font-medium text-white border-r border-slate-700 min-w-[150px] ${getAmmoGradient(weapon)}`}>
                            {weapon.name}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-xs">
                            <span className="px-2 py-1 rounded bg-slate-700 text-slate-300 font-medium">
                              {weapon.type || 'OTHER'}
                            </span>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm text-slate-300">
                            {weapon.pellet && weapon.pellet > 1 ? `${weapon.dmg}×${weapon.pellet}` : weapon.dmg}
                            {weapon.burst && <span className="text-xs text-purple-400 ml-1">×{weapon.burst}</span>}
                            {weapon.hammer && <span className="text-xs text-pink-400 ml-1">×{weapon.hammer}</span>}
                            {weapon.disruptor && <span className="text-xs text-pink-400 ml-1">×{weapon.disruptor}</span>}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm text-slate-300">
                            {weapon.rpm_base
                              ? `${weapon.rpm_base}→${weapon.rpm}`
                              : getEffectiveRPM(weapon).toFixed(0)
                            }
                            {weapon.burst && !weapon.rpm_base && <span className="text-xs text-purple-400 ml-1">(実効)</span>}
                            {weapon.rpm_base && <span className="text-xs text-cyan-400 ml-1">({weapon.accel}s)</span>}
                            {weapon.delay && <span className="text-xs text-yellow-400 ml-1">(+{weapon.delay}s)</span>}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm font-bold text-green-400">
                            {weapon.dps.toFixed(2)}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm font-bold text-yellow-400">
                            {weapon.ttk150.toFixed(3)}s
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm font-bold text-yellow-400">
                            {weapon.ttk175.toFixed(3)}s
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm font-bold text-yellow-400">
                            {weapon.ttk200.toFixed(3)}s
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm font-bold text-yellow-400">
                            {weapon.ttk225.toFixed(3)}s
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className='w-full lg:w-[240px] min-h-[240px] flex-shrink-0 bg-slate-800'>
              <AdsenseAd></AdsenseAd>
            </div>

          </div>
        </div>

        {/* 計算式の説明 */}
        <div className="mt-6 bg-slate-800 rounded-lg p-4 lg:p-6 border border-slate-700">
          <p className="text-sm text-slate-400 mt-3">
            最新のパッチノートを適用しています。
          </p>
          <p className="text-sm text-slate-400 mt-3">
            *印の付いている武器は特殊なダメージ処理のため、TTKの計算が正確ではありません。
          </p>
        </div>
      </div>
    </div>
  );
};

export default ApexDPS;
